import React, { useState, useEffect, useRef } from 'react';
import { File, X, Eye, ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight, List } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { FilePreviewData, formatFileSize, cleanupObjectURL } from '../utils/filePreview';

// Set up PDF.js worker
if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

interface DocumentPreviewProps {
  file: File;
  onRemove: () => void;
  onReplace: () => void;
}

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({ 
  file, 
  onRemove, 
  onReplace 
}) => {
  const [previewData, setPreviewData] = useState<FilePreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [scale, setScale] = useState(1.0);
  
  // Multi-page PDF state
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set());
  const [showAllPages, setShowAllPages] = useState(false);
  const [jumpToPage, setJumpToPage] = useState('');
  
  const currentUrlRef = useRef<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Add scroll listener to update current page when user scrolls manually
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !showAllPages || !numPages) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const containerHeight = scrollContainer.clientHeight;
      const centerPosition = scrollTop + containerHeight / 2;
      
      // Find which page is closest to the center
      let closestPage = 1;
      let minDistance = Infinity;
      
      for (let i = 1; i <= numPages; i++) {
        const pageElement = document.getElementById(`page-${i}`);
        if (pageElement) {
          const pageTop = pageElement.offsetTop;
          const pageCenter = pageTop + pageElement.offsetHeight / 2;
          
          const distance = Math.abs(pageCenter - centerPosition);
          if (distance < minDistance) {
            minDistance = distance;
            closestPage = i;
          }
        }
      }
      
      if (closestPage !== currentPage) {
        setCurrentPage(closestPage);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [showAllPages, numPages, currentPage]);

  useEffect(() => {
    const loadPreview = async () => {
      // Clean up previous URL if it exists
      if (currentUrlRef.current) {
        cleanupObjectURL(currentUrlRef.current);
        currentUrlRef.current = null;
      }

      setIsLoading(true);
      setPdfError(null);
      
      try {
        const { generateFilePreview } = await import('../utils/filePreview');
        const data = await generateFilePreview(file);
        setPreviewData(data);
        
        // Store the URL for cleanup
        if (data.url) {
          currentUrlRef.current = data.url;
        }
      } catch (error) {
        console.error('Error generating preview:', error);
        setPreviewData({
          type: 'unsupported',
          error: 'Failed to generate preview'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadPreview();

    // Cleanup function
    return () => {
      if (currentUrlRef.current) {
        cleanupObjectURL(currentUrlRef.current);
        currentUrlRef.current = null;
      }
    };
  }, [file]);

  const onDocumentLoadSuccess = (pdf: any) => {
    setNumPages(pdf.numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setPdfError('Failed to load PDF preview');
  };

  const onPageLoadSuccess = (pageNumber: number) => {
    setLoadedPages(prev => new Set([...prev, pageNumber]));
  };

  // Navigation functions
  const goToPage = (page: number) => {
    if (page >= 1 && page <= numPages!) {
      setCurrentPage(page);
      
      // In multi-page mode, scroll to the specific page
      if (showAllPages) {
        const scrollContainer = scrollContainerRef.current;
        const pageElement = document.getElementById(`page-${page}`);
        
        if (scrollContainer && pageElement) {
          // Calculate the position to scroll to
          const containerTop = scrollContainer.offsetTop;
          const pageTop = pageElement.offsetTop;
          const scrollTop = pageTop - containerTop - 20; // 20px offset from top
          
          scrollContainer.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        }
      }
      // In single-page mode, just update the current page state
      // The Page component will re-render with the new page number
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < numPages!) {
      goToPage(currentPage + 1);
    }
  };

  const handleJumpToPage = () => {
    const pageNum = parseInt(jumpToPage);
    if (pageNum >= 1 && pageNum <= numPages!) {
      goToPage(pageNum);
      setJumpToPage('');
    }
  };

  // Zoom functions
  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  const renderPreview = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!previewData) {
      return (
        <div className="flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">No preview available</p>
        </div>
      );
    }

    if (previewData.error) {
      return (
        <div className="flex items-center justify-center h-64 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <p className="text-red-600 dark:text-red-400">{previewData.error}</p>
        </div>
      );
    }

    switch (previewData.type) {
      case 'pdf':
        return (
          <div className="space-y-4">
            {/* Controls Header */}
            <div className="flex flex-col space-y-3">
              {/* Zoom Controls */}
              <div className="flex items-center justify-center space-x-2 bg-gray-50 dark:bg-gray-600 rounded-lg p-2">
                <button
                  onClick={zoomOut}
                  disabled={scale <= 0.5}
                  className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Zoom Out"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                
                <button
                  onClick={resetZoom}
                  className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-500 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-400"
                  title="Reset Zoom"
                >
                  <RotateCcw className="h-3 w-3 inline mr-1" />
                  {Math.round(scale * 100)}%
                </button>
                
                <button
                  onClick={zoomIn}
                  disabled={scale >= 3.0}
                  className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Zoom In"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
              </div>

              {/* Page Navigation Controls */}
              {numPages && (
                <div className="flex flex-col space-y-2">
                  {/* Toggle between single page and all pages - only show if more than 1 page */}
                  {numPages > 1 && (
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={() => setShowAllPages(!showAllPages)}
                        className={`px-3 py-1 text-sm rounded flex items-center space-x-1 ${
                          showAllPages 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <List className="h-3 w-3" />
                        <span>{showAllPages ? 'Show Single Page' : 'Show All Pages'}</span>
                      </button>
                    </div>
                  )}

                  {/* Page Navigation - show for all PDFs */}
                  <div className="flex items-center justify-center space-x-2">
                    <button 
                      onClick={goToPreviousPage}
                      disabled={currentPage <= 1}
                      className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Previous Page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    
                    <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm">
                      {currentPage} / {numPages}
                    </span>
                    
                    <button 
                      onClick={goToNextPage}
                      disabled={currentPage >= numPages}
                      className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Next Page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Jump to Page - only show if more than 1 page */}
                  {numPages > 1 && (
                    <div className="flex items-center justify-center space-x-2">
                      <input
                        type="number"
                        value={jumpToPage}
                        onChange={(e) => setJumpToPage(e.target.value)}
                        placeholder="Page #"
                        className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                        min="1"
                        max={numPages}
                      />
                      <button 
                        onClick={handleJumpToPage}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                      >
                        Go
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Page Counter */}
              {numPages && (
                <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                  {numPages} page{numPages > 1 ? 's' : ''} 
                  {showAllPages ? ' • Scroll to view all pages' : ' • First page preview'}
                </div>
              )}
            </div>

            {/* PDF Container */}
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
              {pdfError ? (
                <div className="text-center text-red-600 dark:text-red-400">
                  <p>{pdfError}</p>
                  <p className="text-sm mt-2">PDF preview not available</p>
                </div>
              ) : (
                <div 
                  ref={scrollContainerRef}
                  className={showAllPages ? "max-h-96 overflow-y-auto space-y-4 pdf-scroll-container" : "space-y-2"}
                >
                  <Document
                    file={previewData.url!}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    className="flex justify-center"
                  >
                    {showAllPages && numPages ? (
                      // Render all pages in scrollable container
                      Array.from({ length: numPages }, (_, index) => (
                        <div 
                          key={index + 1} 
                          id={`page-${index + 1}`} 
                          className={`mb-4 last:mb-0 transition-all duration-300 ${
                            currentPage === index + 1 
                              ? 'ring-2 ring-blue-500 ring-opacity-50 bg-blue-50 dark:bg-blue-900/20' 
                              : ''
                          }`}
                        >
                          {!loadedPages.has(index + 1) && (
                            <div className="flex justify-center items-center h-64 bg-gray-200 dark:bg-gray-600 rounded mb-2">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                          )}
                          
                          <Page 
                            pageNumber={index + 1}
                            width={300}
                            scale={scale}
                            className="shadow-lg"
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onLoadSuccess={() => onPageLoadSuccess(index + 1)}
                          />
                          
                          {/* Page number indicator */}
                          <div className="text-center mt-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              currentPage === index + 1
                                ? 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-800'
                                : 'text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800'
                            }`}>
                              Page {index + 1} of {numPages}
                              {currentPage === index + 1 && ' (Current)'}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      // Render single page (current page based on navigation)
                      <div>
                        <Page 
                          pageNumber={currentPage} 
                          width={300}
                          scale={scale}
                          className="shadow-lg"
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                        
                        {numPages && numPages > 1 && (
                          <div className="text-center mt-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              Page {currentPage} of {numPages}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </Document>
                </div>
              )}
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
            <img
              src={previewData.url!}
              alt="Preview"
              className="max-w-full max-h-64 mx-auto rounded shadow-lg"
            />
          </div>
        );

      case 'text':
        return (
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
            <div className="max-h-64 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                {previewData.content}
              </pre>
            </div>
          </div>
        );

      case 'doc':
        return (
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <File className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <p className="text-gray-700 dark:text-gray-300 font-medium">Microsoft Word Document</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Preview not available for Word documents
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <File className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-700 dark:text-gray-300 font-medium">Unsupported File Type</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Preview not available for this file type
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <File className="h-6 w-6 text-blue-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {file.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatFileSize(file.size)} • {file.type || 'Unknown type'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={onReplace}
            className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            title="Replace file"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={onRemove}
            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            title="Remove file"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="p-4">
        {renderPreview()}
      </div>
    </div>
  );
};
